import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Response, Http } from '@angular/http';
import { AgencyConfigLookup } from 'app/event/models/config/agency-config-lookup';
import { ConfigRequest } from 'app/event/models/config/config-request';
import { ConfigResponse } from 'app/event/models/config/config-response';
import { CountryConfigLookup } from 'app/event/models/config/country-config-lookup';
import { LegalEntity } from 'app/event/models/legal-entity';
import { LegalEntitySearch } from 'app/event/models/legal-entity-search';
import { Observable } from 'rxjs/Observable';
import { catchError, mapTo, retry } from 'rxjs/operators';
import { EnvironmentConfiguration } from '../../environment/services/environment.configuration';
import { PaymentRequestActionData } from '../../event/models/payment-request-action-data';
import { Constants } from '../../shared/constants';
import { ResponseUtilService } from '../../shared/utils/response-util.service';
import { PaymentRequest } from '../model/payment-request';
import { Attachment } from 'app/event/models/attachment';
import { List } from 'immutable';
import { AuthConfig, AuthHttp } from 'angular2-jwt';
import { UserLocalStorageService } from '../../permission/services/user.local.storage.service';

@Injectable()
export class CommissionService {

  eventsGetUrl: string = this.environment.baseUrl + '/api/commissions/events';
  approvePostUrl: string = this.environment.baseUrl + '/api/commissions/approve';
  rejectPostUrl: string = this.environment.baseUrl + '/api/commissions/reject';
  commissionOoivUrl: string = this.environment.baseUrl + '/api/commissions/ooiv';
  legalEntityUrl: string = this.environment.baseUrl + '/api/commissions/legal';
  attachmentsGetUrl: string = this.environment.baseUrl + '/api/commissions/attachments';
  commissionsUrl: string = this.environment.baseUrl + '/api/commissions';
  readonly isResendPaymentRequestAllowedUrl = `${this.environment.baseUrl}/api/v1/payment-request/resend-allowed`;
  readonly resendPaymentRequestUrl = `${this.environment.baseUrl}/api/v1/payment-request/resend`;

  constructor(
    private httpClient: HttpClient,
    private http: Http,
    private environment: EnvironmentConfiguration,
    private userLocalStorageService: UserLocalStorageService,
    private responseUtilService: ResponseUtilService
  ) {
  }

  getPaymentRequestDetails(): Observable<PaymentRequest | Response> {
    const options = this.buildHttpHeaders();
    return this.httpClient.get<PaymentRequest>(this.eventsGetUrl, options)
      .pipe(
        retry(1));
  }

  approvePaymentRequest(email: string, legalEntity: LegalEntity, attachmentId: string): Observable<any> {
    const options = this.buildHttpHeaders();
    const request = new PaymentRequestActionData({
      email: email,
      reason: null,
      details: null,
      legalEntity: legalEntity,
      attachmentId: attachmentId
    });

    const requestAsJSON = JSON.stringify(request);
    return this.httpClient.post(this.approvePostUrl, requestAsJSON, options)
      .pipe(
        retry(1),
        catchError(err =>
          this.responseUtilService.handleError(err)
        ));
  }

  rejectPaymentRequest(email: string, reason: string, details: string, attachmentId: string): Observable<any> {
    const options = this.buildHttpHeaders();
    const request = new PaymentRequestActionData({
      email: email,
      reason: reason,
      details: details,
      attachmentId: attachmentId
    });
    const requestAsJSON = JSON.stringify(request);
    return this.httpClient.post(this.rejectPostUrl, requestAsJSON, options)
      .pipe(
        retry(1),
        catchError(err => this.responseUtilService.handleError(err)));
  }

  private buildHttpHeaders() {
    let token = localStorage.getItem(Constants.COMMISSION_TOKEN_KEY);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': token
    });
    let options = {headers: headers};
    return options;
  }

  public isOoivEnabled(agencyId: string, agencyCountry: string, vendorCountry: string): Observable<boolean> {
    return this.getCommissionOoivEnabled(this.buildConfigOoivRequest(agencyId, agencyCountry, vendorCountry))
      .map((response: ConfigResponse) => {
        return response ? JSON.parse(response.value) : null;
      });
  }

  getCommissionOoivEnabled(request: ConfigRequest): Observable<any> {
    const options = this.buildHttpHeaders();
    return this.httpClient.post(this.commissionOoivUrl, JSON.stringify(request.toJS()), options)
      .pipe(
        retry(1),
        catchError(err => this.responseUtilService.handleError(err)));
  }

  buildConfigOoivRequest(agencyId: string, agencyCountry: string, vendorCountry): ConfigRequest {
    let agencyConfigLookup = null;
    let countryConfigLookup = null;

    if (agencyId != null) {
      agencyConfigLookup = new AgencyConfigLookup({agencyId: agencyId});
    }

    if (agencyCountry != null || vendorCountry != null) {
      countryConfigLookup = new CountryConfigLookup({agencyCountry: agencyCountry, vendorCountry: vendorCountry});
    }

    return new ConfigRequest({
      config: null,
      property: null,
      agency: agencyConfigLookup,
      country: countryConfigLookup
    });
  }

  getLegalEntityInfo(legalEntitySearch: LegalEntitySearch): Observable<any> {
    const options = this.buildHttpHeaders();
    const requestAsJSON = JSON.stringify(legalEntitySearch);
    return this.httpClient.post(this.legalEntityUrl, requestAsJSON, options)
      .pipe(
        retry(1),
        catchError(err => this.responseUtilService.handleError(err)));
  }

  getAttachments(): Observable<List<Attachment> | Response> {
    const options = this.buildHttpHeaders();
    return this.httpClient.get<List<Attachment>>(this.attachmentsGetUrl, options);
  }

  addAttachment(eventId: number, eventPartyId: number, file: File, multiVendor: boolean): Observable<any> {
    let formData: FormData = new FormData();
    formData.append('file', file);
    return this.buildHttpHeadersFile.post(this.commissionsUrl + '/' + eventId + '/' + eventPartyId + '/attachments', formData)
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  getAttachment(uniqueId: string): Observable<string> {
    return this.buildHttpHeadersFile.get(`${this.commissionsUrl}/attachments/${uniqueId}`)
      .map(resp => resp.text())
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  deleteAttachment(uniqueId: string): Observable<any> {
    return this.buildHttpHeadersFile.delete(`${this.commissionsUrl}/attachments/${uniqueId}`)
      .catch(response => this.responseUtilService.handleError(response)).share();
  }

  get buildHttpHeadersFile() {
    let token = localStorage.getItem(Constants.COMMISSION_TOKEN_KEY);

    const configPersonalizado = new AuthConfig({
      noTokenScheme: true,
      tokenName: Constants.COMMISSION_TOKEN_KEY,
      tokenGetter: (() => token),
      globalHeaders: [
        {'Cache-Control': 'no-cache, no-store, must-revalidate'},
        {'Pragma': 'no-cache'},
        {'Expires': 'Thu, 01 Jan 1970 00:00:00 GMT'},
        {'If-Modified-Since': '0'}
      ],
    });

    return new AuthHttp(configPersonalizado, this.http);
  }

  isResendPaymentRequestAllowed(): Observable<{ resendPaymentRequestAllowed: boolean; }> {
    const body = this.userLocalStorageService.getItem(Constants.COMMISSION_TOKEN_KEY);
    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };
    return this.httpClient.post<{ resendPaymentRequestAllowed: boolean; }>(this.isResendPaymentRequestAllowedUrl, body, options);
  }

  resendPaymentRequest(): Observable<{}> {
    const body = this.userLocalStorageService.getItem(Constants.COMMISSION_TOKEN_KEY);
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      responseType: 'text' as 'text'
    };
    return this.httpClient.post(this.resendPaymentRequestUrl, body, options).pipe(mapTo({}));
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/payment/service/commission.service.ts